import { confirmationValid } from "utils/noteReview";
import { CircularProgress, Dialog } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useMaterialUIController } from "context";
import AppHeader from "components/AppHeader";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import ThemedSelect from "components/FormControls/ThemedSelect";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import { adjustmentRequest, loadNoteSources } from "utils/adjustmentNoteAPI";
import { savePdfResponse } from "utils/documentPdf";
import ViewCredit from "../CreditNote/ViewCredit";
import CreditNoteDetailSkeleton from "../CreditNote/CreditNoteDetailSkeleton";
import ActivityLog from "layouts/activityLog/activityLog";
import { sourcePath } from "./NoteRegister";
import NoteEmailPreview from "./NoteEmailPreview";
import NoteEmailError, { emailErrorFeedback } from "./NoteEmailError";
import "../CreditNote/creditNoteDetail.css";
import "../DebitNote/debitNote.css";
import "./adjustmentNotes.css";
import "./noteDesign.css";
export default function NoteDetail({ credit = false }) {
  const { id } = useParams(),
    location = useLocation(),
    [controller] = useMaterialUIController();
  const kind = credit ? "credit" : "debit",
    base = credit ? "/creditNote" : "/debitNote";
  const routeKey = `${kind}:${id}`;
  const activeRoute = useRef(routeKey);
  activeRoute.current = routeKey;
  const requestSequence = useRef(0);
  const [response, setData] = useState(null),
    [error, setError] = useState(""),
    [historyLoading, setHistoryLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [generatingPdf, setGeneratingPdf] = useState(false),
    [open, setOpen] = useState(false),
    [message, setMessage] = useState("");
  const data = response?.routeKey === routeKey ? response : null;
  const [modal, setModal] = useState("");
  const [deliveryPreview, setDeliveryPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [deliveryError, setDeliveryError] = useState(null);
  useEffect(() => {
    setDeliveryError(null);
  }, [modal, routeKey]);
  const openedRouteAction = useRef("");
  const [sourceReview, setSourceReview] = useState(null);
  const [confirmation, setConfirmation] = useState({
    mode: "post",
    reference: "",
    amount: "",
    date: new Date().toLocaleDateString("en-CA", {
      timeZone: "Australia/Sydney",
    }),
    difference: false,
    consent: false,
  });
  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    const next = await adjustmentRequest(
      credit ? "credit" : "debit",
      `/${id}`,
      undefined,
      "get"
    );
    if (
      activeRoute.current === routeKey &&
      sequence === requestSequence.current
    )
      setData({ ...next, routeKey });
    return next;
  }, [id, credit, routeKey]);
  const invalidateRequests = useCallback(() => {
    requestSequence.current++;
  }, []);
  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    setModal("");
    setSourceReview(null);
    setMessage("");
    setBusy(false);
    load().catch((e) => {
      if (active) setError(e.response?.data?.Result_Message || e.message);
    });
    return () => {
      active = false;
      invalidateRequests();
    };
  }, [load, invalidateRequests]);
  const raw = data?.note;
  useEffect(() => {
    let active = true;
    setDeliveryPreview(null);
    setPreviewError("");
    if (modal !== "delivery") return undefined;
    adjustmentRequest(kind, `/${id}/delivery-preview`)
      .then((preview) => {
        if (!preview?.recipient || !preview?.subject || !preview?.html || !Array.isArray(preview.attachments))
          throw new Error("The email preview could not be loaded. Please try again.");
        if (active) setDeliveryPreview(preview);
      })
      .catch((e) => {
        if (active) setPreviewError(e.response?.data?.Result_Message || e.message);
      });
    return () => { active = false; };
  }, [kind, id, modal, previewAttempt]);
  const note =
    raw &&
    (credit
      ? {
          ...raw,
          number: raw.creditNumber,
          status: raw.activityStatus,
          sourceID: raw.sourceInvoiceID,
          sourceKind: "invoice",
          reference: raw.sourceSnapshot?.reference,
          contactName: raw.customerIdentity?.name,
          currency: raw.currencyValue,
          total: raw.finalAmount,
          notes: raw.internalNotes,
          createdAt: raw.createdDate,
        }
      : raw);
  const money = (value) =>
    new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: note?.currency === "$" ? "AUD" : note?.currency || "AUD",
    }).format(value || 0);
  const timestamp = (date) =>
    new Date(date).toLocaleString("en-AU", {
      timeZone: "Australia/Sydney",
      timeZoneName: "short",
    });
  const action = async (operation, emailAction = false) => {
    if (busy) return;
    setBusy(true);
    setError("");
    setDeliveryError(null);
    setMessage("");
    try {
      await operation();
    } catch (e) {
      if (emailAction) setDeliveryError(emailErrorFeedback(e));
      else setError(e.response?.data?.Result_Message || e.message);
      await load().catch(() => {});
    } finally {
      setBusy(false);
    }
  };
  const pdf = () =>
    action(async () => {
      setGeneratingPdf(true);
      try {
        const output = await adjustmentRequest(kind, `/${id}/pdf`);
        savePdfResponse({ data: output.pdf }, output.filename);
      } finally {
        setGeneratingPdf(false);
      }
    });
  const deliver = () =>
    action(async () => {
      if (!deliveryPreview || previewError) return;
      await adjustmentRequest(kind, `/${id}/deliver`, { confirm: true });
      setModal("");
      showDraftSavedMessage(`${credit ? "Credit" : "Debit"} note email sent successfully`);
      await load();
    }, true);
  const reconcile = () =>
    action(async () => {
      await adjustmentRequest("debit", `/${id}/reconcile`, {
        revision: note.revision,
        confirmationReference: confirmation.reference,
        confirmedAmount: confirmation.amount,
        confirmationDate: confirmation.date,
        acceptDifference: confirmation.difference,
        confirm: confirmation.consent,
        mode: confirmation.mode,
        expectedBalance: sourceReview?.balance,
        sourceRevision: sourceReview?.revision,
      });
      await load();
      setModal("");
      setMessage(
        "Supplier confirmation applied. The supporting reference and amount are saved in the audit history."
      );
    });
  const beginConfirmation = async (mode) => {
    setConfirmation({
      ...confirmation,
      mode,
      amount: String(note.total),
      consent: false,
      difference: false,
    });
    setSourceReview(null);
    setError("");
    setModal("confirmation");
    setBusy(true);
    try {
      const sources = await loadNoteSources("supplier", { id: note.sourceID });
      const selected = sources.find(
        (row) =>
          row.id === String(note.sourceID) && row.kind === note.sourceKind
      );
      if (!selected)
        throw new Error(
          "The linked source is no longer eligible. Review the original document before continuing."
        );
      setSourceReview(selected);
    } catch (e) {
      setError(e.response?.data?.Result_Message || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("action");
    if (!requested) { openedRouteAction.current = ""; return; }
    const key = `${routeKey}:${requested}`;
    if (credit || !data?.canWrite || !note || openedRouteAction.current === key) return;
    if (requested === "delivery" && note.status !== "Draft") {
      openedRouteAction.current = key;
      setModal("delivery");
    } else if (["confirm", "link"].includes(requested) && note.direction === "supplier" && note.status === "Awaiting confirmation") {
      openedRouteAction.current = key;
      beginConfirmation(requested === "link" || note.sourceKind !== "expense" ? "link" : "post");
    }
  }, [credit, data, note, routeKey, location.search, beginConfirmation]);

  const hasNote = Boolean(raw);
  useEffect(() => {
    if (credit || !hasNote || !["#activity", "#supporting-adjustment"].includes(location.hash)) return undefined;
    const frame = requestAnimationFrame(() => document.getElementById(location.hash.slice(1))?.scrollIntoView?.({ block: "start" }));
    return () => cancelAnimationFrame(frame);
  }, [credit, hasNote, routeKey, location.hash]);

  if (credit && raw && !raw.sourceInvoiceID) return <ViewCredit initialRecord={raw} />;
  return (
    <div className={`credit-detail-shell${credit ? "" : " debit-design-detail"}${controller.darkMode ? " dark" : ""}`}>
      <DashboardNavigation
        activePath={base}
        open={open}
        onClose={() => setOpen(false)}
      />
      <div className="credit-detail-main">
        <AppHeader onOpenNav={() => setOpen(true)} />
        <main className="credit-detail-page adjustment-note-detail-page">
          <nav className="credit-detail-breadcrumb">
            <Link to={base}>{credit ? "Credit notes" : "Debit notes"}</Link>
            <span>/</span>
            <span>{note?.number || "Details"}</span>
          </nav>
          {location.state?.notice && (
            <p
              role={
                location.state.noticeType === "success" ? "status" : "alert"
              }
              className={
                location.state.noticeType === "success"
                  ? "note-feedback"
                  : "credit-detail-error"
              }
            >
              {location.state.notice}
            </p>
          )}
          {error && (
            <p role="alert" className="credit-detail-error">
              {error}{" "}
              {!data && <button onClick={() => action(load)}>Retry</button>}
            </p>
          )}
          {message && <p role="status">{message}</p>}
          {!error && (!data || historyLoading) && <CreditNoteDetailSkeleton label={`Loading ${credit ? "credit" : "debit"} note details…`} />}
          <div className={`credit-detail-loaded${!data || historyLoading ? " is-loading" : ""}`}>
            {note && (
              <>
                <header className="credit-detail-heading">
                  <div>
                    <h1>{note.number}</h1>
                    <p>
                      {credit
                        ? "Credit against an issued invoice"
                        : note.direction === "customer"
                        ? "Customer supplementary charge"
                        : "Supplier adjustment request"}
                    </p>
                  </div>
                  <div className="credit-detail-actions">
                    {data.canWrite && note.status === "Draft" && (
                      <Link
                        className="credit-detail-button primary"
                        to={`${base}/edit/${id}`}
                      >
                        {credit
                          ? "Review and apply"
                          : note.direction === "customer"
                          ? "Review and issue"
                          : "Review and notify"}
                      </Link>
                    )}
                    <button
                      className="credit-detail-button"
                      disabled={busy}
                      onClick={pdf}
                    >
                      {generatingPdf && (
                        <CircularProgress
                          size={16}
                          color="inherit"
                          aria-hidden="true"
                        />
                      )}
                      {generatingPdf ? "Generating PDF…" : "Download PDF"}
                    </button>
                    {data.canWrite &&
                      (credit
                        ? note.status === "Applied"
                        : note.direction === "customer" &&
                          note.status === "Issued") && (
                        <button
                          className="credit-detail-button primary"
                          disabled={busy || note.deliveryStatus === "sending"}
                          onClick={() => setModal("delivery")}
                        >
                          {note.deliveryStatus === "failed"
                            ? "Retry delivery"
                            : note.deliveryStatus === "sent"
                            ? "Resend email"
                            : "Send email"}
                        </button>
                      )}
                  </div>
                </header>
                <section className="credit-detail-card credit-detail-document">
                  <header>
                    <h2>{note.contactName}</h2>
                    <span
                      className={`note-status note-status-${note.status
                        .toLowerCase()
                        .replaceAll(" ", "-")}`}
                    >
                      {note.status}
                    </span>
                  </header>
                  <div className="credit-detail-identities">
                    <div>
                      <span>Linked document</span>
                      <h3>
                        <Link to={sourcePath(note)}>{note.reference}</Link>
                      </h3>
                      <p>{note.reason}</p>
                    </div>
                    <dl>
                      <dt>Created</dt>
                      <dd>{timestamp(note.createdAt)}</dd>
                      {note.issuedAt && (
                        <>
                          <dt>Issued</dt>
                          <dd>{timestamp(note.issuedAt)}</dd>
                        </>
                      )}
                      {note.appliedAt && (
                        <>
                          <dt>Applied</dt>
                          <dd>{timestamp(note.appliedAt)}</dd>
                        </>
                      )}
                      {note.notifiedAt && (
                        <>
                          <dt>Email accepted</dt>
                          <dd>{timestamp(note.notifiedAt)}</dd>
                        </>
                      )}
                      {note.lastEmailSentDate && (
                        <>
                          <dt>Email accepted</dt>
                          <dd>{timestamp(note.lastEmailSentDate)}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                  <div className="credit-detail-table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit amount</th>
                          <th>Included GST</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {note.lines?.map((line, index) => (
                          <tr key={index}>
                            <td>{line.description}</td>
                            <td>{line.quantity}</td>
                            <td>{money(line.unitAmount)}</td>
                            <td>{money(line.gstAmount)}</td>
                            <td>{money(line.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="credit-detail-totals">
                    <h2>
                      {credit
                        ? "Total credit"
                        : note.direction === "supplier"
                        ? "Requested reduction"
                        : "Total debit"}{" "}
                      {money(note.total)}
                    </h2>
                  </div>
                  {!credit && note.direction === "supplier" && note.status === "Applied" && (
                    <section id="supporting-adjustment" className="credit-detail-notes">
                      <h3>Supporting adjustment</h3>
                      <p>{note.confirmationMode === "post" ? "Confirmed reduction posted once" : "Existing supplier adjustment linked without another posting"}</p>
                      <dl className="debit-supporting-facts">
                        <dt>Reference</dt><dd>{note.confirmationReference || "Not recorded"}</dd>
                        <dt>Confirmed amount</dt><dd>{money(note.confirmedAmount ?? note.total)}</dd>
                        <dt>Confirmation date</dt><dd>{note.confirmationDate ? timestamp(note.confirmationDate) : "Not recorded"}</dd>
                      </dl>
                      <Link to={sourcePath(note)}>View linked document</Link>
                    </section>
                  )}
                  <section className="credit-detail-notes">
                    <h3>Balance treatment</h3>
                    <p>
                      {note.status === "Draft"
                        ? "Draft only. The linked document balance is unchanged."
                        : credit
                        ? `Applied credit: ${money(
                            note.appliedToBalance
                          )} deducted from the invoice balance.${
                            note.unallocatedAmount
                              ? ` Unapplied customer credit: ${money(
                                  note.unallocatedAmount
                                )}. No refund was recorded.`
                              : ""
                          }`
                        : note.status === "Applied"
                        ? `Confirmed reduction of ${money(
                            note.confirmedAmount ?? note.total
                          )} ${
                            note.confirmationMode === "post"
                              ? "posted once to the supplier expense"
                              : "linked to the existing supplier adjustment"
                          }. Supporting reference: ${
                            note.confirmationReference
                          }.`
                        : note.direction === "customer"
                        ? "The supplementary charge was posted once to the invoice balance. Issued is not Paid. Email delivery is separate from this posting."
                        : "Request only. The recorded payable remains unchanged until a confirmed adjustment is applied or linked."}
                    </p>
                    {note.deliveryStatus && (
                      <p>
                        Email delivery: {note.deliveryStatus}
                        {note.deliveryStatus === "failed"
                          ? ". The financial adjustment is saved; retry delivery without creating a new note."
                          : ""}
                      </p>
                    )}
                    {!credit &&
                      data.canWrite &&
                      note.status === "Awaiting confirmation" && (
                        <div className="credit-detail-actions">
                          {note.sourceKind === "expense" && (
                            <button
                              className="credit-detail-button primary"
                              disabled={busy}
                              onClick={() => beginConfirmation("post")}
                            >
                              Review supplier confirmation
                            </button>
                          )}
                          <button
                            className="credit-detail-button"
                            disabled={busy}
                            onClick={() => beginConfirmation("link")}
                          >
                            Link existing adjustment
                          </button>
                          {note.sourceKind === "rcti" && (
                            <Link
                              className="credit-detail-button"
                              to={`/rcti/view/${note.sourceID}`}
                            >
                              Open RCTI adjustment
                            </Link>
                          )}
                        </div>
                      )}
                    {note.notes && (
                      <>
                        <h3>
                          Internal notes · Not visible to{" "}
                          {note.direction === "supplier"
                            ? "supplier"
                            : "customer"}
                        </h3>
                        <p>{note.notes}</p>
                      </>
                    )}
                  </section>
                </section>
                <section
                  className="credit-detail-card credit-detail-activity"
                  id="activity"
                >
                  <ActivityLog
                    key={`${kind}:${id}`}
                    name={credit ? "Credit Notes" : "Debit Notes"}
                    variant="detail"
                    onLoadingChange={setHistoryLoading}
                  />
                  <h2 className="note-detail-events-legacy">Audit history</h2>
                  <ol className="note-detail-events note-detail-events-legacy">
                    <li>Draft created · {timestamp(note.createdAt)}</li>
                    {note.issuedAt && (
                      <li>
                        Customer charge issued · {timestamp(note.issuedAt)}
                      </li>
                    )}
                    {note.appliedAt && (
                      <li>Credit applied · {timestamp(note.appliedAt)}</li>
                    )}
                    {note.notifiedAt && (
                      <li>Email accepted · {timestamp(note.notifiedAt)}</li>
                    )}
                    {note.reconciledAt && (
                      <li>
                        Supplier reduction{" "}
                        {money(note.confirmedAmount ?? note.total)}{" "}
                        {note.confirmationMode === "post" ? "posted" : "linked"}{" "}
                        · {timestamp(note.reconciledAt)} ·{" "}
                        {note.confirmationReference}
                        {note.confirmationDate &&
                          ` · Confirmation dated ${timestamp(
                            note.confirmationDate
                          )}`}
                      </li>
                    )}
                  </ol>
                </section>
              </>
            )}
          </div>
        </main>
      </div>
      {modal && note && (
        <Dialog
          open
          onClose={() => !busy && setModal("")}
          aria-labelledby="note-action-title"
          maxWidth={modal === "delivery" ? "md" : "sm"}
          fullWidth
          PaperProps={{ className: `debit-modal${credit ? "" : " debit-design-modal"}${modal === "delivery" ? " note-email-dialog" : ""}` }}
        >
          <h2 id="note-action-title">
            {modal === "delivery"
              ? "Send saved note by email?"
              : confirmation.mode === "post"
              ? "Review supplier confirmation"
              : "Link existing supplier adjustment"}
          </h2>
          {error && (
            <p role="alert" className="credit-detail-error">
              {error}
            </p>
          )}
          {modal === "delivery" ? (
            <>
              <p>Review the recipient, email and PDF attachment before sending.</p>
              {previewError ? (
                <div className="note-email-preview-error">
                  <p role="alert">{previewError}</p>
                  <button className="credit-detail-button" onClick={() => setPreviewAttempt((attempt) => attempt + 1)}>
                    Retry preview
                  </button>
                </div>
              ) : !deliveryPreview ? (
                <div className="note-email-preview-loading" role="status">
                  <CircularProgress size={20} color="inherit" aria-hidden="true" />
                  Loading email preview…
                </div>
              ) : (
                <section className="note-email-review" aria-label="Email preview">
                  <dl className="note-email-fields">
                    <div><dt>To</dt><dd>{deliveryPreview.recipientName && <span>{deliveryPreview.recipientName}<br /></span>}{deliveryPreview.recipient}<NoteEmailError error={deliveryError} field="recipient" /></dd></div>
                    <div><dt>Subject</dt><dd>{deliveryPreview.subject}</dd></div>
                    <div><dt>Attachments</dt><dd>{deliveryPreview.attachments.map((attachment) => (
                      <span className="note-email-attachment" key={attachment.filename}>{attachment.filename} · PDF</span>
                    ))}
                      <p className="note-email-attachment-hint">The note PDF is attached automatically when you send this email.</p>
                      <NoteEmailError error={deliveryError} field="attachments" />
                    </dd></div>
                  </dl>
                  <h3>Email message</h3>
                  <NoteEmailPreview
                    title={`${credit ? "Credit" : "Debit"} note email preview`}
                    html={deliveryPreview.html}
                  />
                </section>
              )}
              <NoteEmailError error={deliveryError} field="delivery" />
              <div className="debit-modal-actions">
                <button
                  className="credit-detail-button"
                  disabled={busy}
                  onClick={() => setModal("")}
                >
                  Cancel
                </button>
                <button
                  className="credit-detail-button primary"
                  disabled={busy || !deliveryPreview || Boolean(previewError)}
                  onClick={deliver}
                >
                  {busy && (
                    <CircularProgress
                      size={16}
                      color="inherit"
                      aria-hidden="true"
                    />
                  )}
                  {busy ? "Sending…" : "Confirm and send"}
                </button>
              </div>
            </>
          ) : (
            <>
              <p>
                {note.reference} · Requested reduction {money(note.total)}.{" "}
                {confirmation.mode === "post"
                  ? "Only the confirmed amount will reduce the expense payable."
                  : "The linked document must already contain this reduction. Linking does not post it again."}
              </p>
              {sourceReview && (
                <div className="note-confirm-balance">
                  <p>
                    Recorded amount owed{" "}
                    <strong>{money(sourceReview.balance)}</strong>
                  </p>
                  <p>
                    Confirmed reduction{" "}
                    <strong>−{money(confirmation.amount)}</strong>
                  </p>
                  <p>
                    {confirmation.mode === "post"
                      ? "Projected amount owed after confirmation"
                      : "Recorded payable (linking does not repost)"}
                    <strong>
                      {confirmation.mode === "post" &&
                      (!/^\d+(?:\.\d{1,2})?$/.test(confirmation.amount) ||
                        Number(confirmation.amount) <= 0 ||
                        Number(confirmation.amount) > sourceReview.balance ||
                        Number(confirmation.amount) > sourceReview.allowance)
                        ? "Unavailable"
                        : money(
                            confirmation.mode === "post"
                              ? Math.max(
                                  0,
                                  sourceReview.balance -
                                    Number(confirmation.amount || 0)
                                )
                              : sourceReview.balance
                          )}
                    </strong>
                  </p>
                </div>
              )}
              <fieldset className="note-confirm-grid" disabled={busy}>
                {note.sourceKind === "expense" && (
                  <label>
                    Confirmation treatment
                    <ThemedSelect
                      aria-label="Confirmation treatment"
                      value={confirmation.mode}
                      onChange={(event) =>
                        setConfirmation({
                          ...confirmation,
                          mode: event.target.value,
                          consent: false,
                        })
                      }
                    >
                      <option value="post">Apply confirmed reduction</option>
                      <option value="link">
                        Link an already recorded adjustment
                      </option>
                    </ThemedSelect>
                  </label>
                )}
                <label>
                  Supporting adjustment or evidence reference
                  <input
                    aria-label="Supplier confirmation reference"
                    maxLength={120}
                    value={confirmation.reference}
                    onChange={(event) =>
                      setConfirmation({
                        ...confirmation,
                        reference: event.target.value,
                        consent: false,
                      })
                    }
                  />
                </label>
                <label>
                  Confirmed reduction amount
                  <input
                    aria-label="Confirmed reduction amount"
                    inputMode="decimal"
                    value={confirmation.amount}
                    onChange={(event) =>
                      setConfirmation({
                        ...confirmation,
                        amount: event.target.value,
                        difference: false,
                        consent: false,
                      })
                    }
                  />
                </label>
                <label>
                  Confirmation date
                  <input
                    aria-label="Confirmation date"
                    type="date"
                    max={new Date().toLocaleDateString("en-CA", {
                      timeZone: "Australia/Sydney",
                    })}
                    value={confirmation.date}
                    onChange={(event) =>
                      setConfirmation({
                        ...confirmation,
                        date: event.target.value,
                        consent: false,
                      })
                    }
                  />
                </label>
                {Number(confirmation.amount) !== Number(note.total) && (
                  <label className="debit-confirm-checkbox">
                    <input
                      type="checkbox"
                      checked={confirmation.difference}
                      onChange={(event) =>
                        setConfirmation({
                          ...confirmation,
                          difference: event.target.checked,
                        })
                      }
                    />
                    I accept that the confirmed amount differs from the
                    requested {money(note.total)}.
                  </label>
                )}
                <label className="debit-confirm-checkbox">
                  <input
                    type="checkbox"
                    checked={confirmation.consent}
                    onChange={(event) =>
                      setConfirmation({
                        ...confirmation,
                        consent: event.target.checked,
                      })
                    }
                  />
                  I reviewed the supporting reference and confirmed amount and
                  approve this adjustment.
                </label>
              </fieldset>
              <div className="debit-modal-actions">
                <button
                  className="credit-detail-button"
                  disabled={busy}
                  onClick={() => setModal("")}
                >
                  Keep reviewing
                </button>
                <button
                  className="credit-detail-button primary"
                  disabled={
                    busy ||
                    !confirmationValid(
                      confirmation,
                      sourceReview,
                      note.total,
                      new Date().toLocaleDateString("en-CA", {
                        timeZone: "Australia/Sydney",
                      })
                    )
                  }
                  onClick={reconcile}
                >
                  {busy && (
                    <CircularProgress
                      size={16}
                      color="inherit"
                      aria-hidden="true"
                    />
                  )}
                  {busy
                    ? "Applying…"
                    : confirmation.mode === "post"
                    ? "Confirm and apply"
                    : "Verify and link"}
                </button>
              </div>
            </>
          )}
        </Dialog>
      )}
    </div>
  );
}
